// Логика левого меню: кто что видит, что выделить, как искать. Чистые функции — покрыты тестами.
import { ADMIN_ROLES, BASE_WEIGHTS, DAILY, FOOTER, GROUPS, ITEMS } from './menuConfig.mjs'
import { managerOnly } from '../pages/admin/registryCycle.mjs'

export const HOT_LIMIT = 5 // сколько пунктов выделять жирным
export const HALF_LIFE_DAYS = 14 // личная частота «забывается» вдвое за две недели
const DAY_MS = 24 * 60 * 60 * 1000

// ctx = { role, isGuest, hasPermission(p), dynamic: { finance: bool } }
export function canSee(item, ctx) {
  if (!item) return false
  const a = item.access || {}
  const isAdmin = ADMIN_ROLES.includes(ctx.role)
  if (a.registryManagerOnly && !managerOnly(ctx.role)) return false
  if (a.nonGuestOnly && ctx.isGuest) return false
  if (a.adminOnly && !isAdmin) return false
  if (a.permission && !ctx.hasPermission(a.permission)) return false
  if (Array.isArray(a.permissionAny) && !a.permissionAny.some((p) => ctx.hasPermission(p))) return false
  if (a.registry && !(isAdmin || ctx.hasPermission('registry.view') || ctx.hasPermission('registry.edit'))) return false
  if (a.dynamic && !(ctx.dynamic && ctx.dynamic[a.dynamic])) return false
  if (ctx.isGuest && item.path !== '/') return false
  return true
}

export function withKey(key) {
  return ITEMS[key] ? { key, ...ITEMS[key] } : null
}

// Личные переходы: { key: { n: «взвешенное число переходов», t: время последнего } }.
export function decayed(entry, now = Date.now()) {
  if (!entry || !entry.n) return 0
  const days = Math.max(0, (now - (entry.t || now)) / DAY_MS)
  return entry.n * Math.pow(0.5, days / HALF_LIFE_DAYS)
}

export function recordVisit(usage = {}, key, now = Date.now()) {
  if (!key || !ITEMS[key]) return usage
  const next = { ...usage }
  next[key] = { n: decayed(usage[key], now) + 1, t: now }
  return next
}

// Итоговый вес: стартовый вес роли + личная частота (каждый переход ≈ 4 очка, потолок 100).
export function score(key, role, usage = {}, now = Date.now()) {
  const base = (BASE_WEIGHTS[role] || BASE_WEIGHTS.default)[key] || 0
  const personal = Math.min(100, decayed(usage[key], now) * 4)
  return base + personal
}

export function hotKeys(keys, role, usage = {}, now = Date.now(), limit = HOT_LIMIT) {
  return keys
    .map((key) => ({ key, s: score(key, role, usage, now) }))
    .filter((x) => x.s >= 20)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.key)
}

// Полное меню для человека: «Каждый день», группы (без дублей), подвал. Пустые группы не показываются.
export function buildMenu(ctx, usage = {}, now = Date.now()) {
  const visible = (key) => canSee(ITEMS[key], ctx)
  const daily = ['home', ...(DAILY[ctx.role] || DAILY.default)].filter(visible)
  const used = new Set(daily)
  const footer = FOOTER.filter((k) => visible(k) && !used.has(k))
  footer.forEach((k) => used.add(k))
  const groups = GROUPS
    .map((g) => ({ ...g, keys: g.items.filter((k) => visible(k) && !used.has(k)) }))
    .filter((g) => g.keys.length)
  const all = [...daily, ...groups.flatMap((g) => g.keys), ...footer]
  const hot = new Set(hotKeys(all.filter((k) => k !== 'home'), ctx.role, usage, now))
  const decorate = (key) => ({ ...withKey(key), hot: hot.has(key) })
  return {
    daily: daily.map(decorate),
    groups: groups.map((g) => ({
      key: g.key, name: g.name, icon: g.icon, hint: g.hint,
      items: g.keys.map(decorate),
      hasHot: g.keys.some((k) => hot.has(k)),
    })),
    footer: footer.map(decorate),
  }
}

// Какой пункт меню сейчас открыт: самый длинный совпавший путь (карточка рейса /trips/123 → «Рейсы»).
export function activeKey(path, keys) {
  let best = null
  let bestLen = -1
  for (const key of keys) {
    const item = ITEMS[key]
    if (!item) continue
    const p = item.path
    const hit = item.exact || p === '/' ? path === p : path === p || path.startsWith(p + '/')
    if (hit && p.length > bestLen) { best = key; bestLen = p.length }
  }
  return best
}

// Поиск по меню: название, пояснение, слова. Без учёта регистра и «ё».
const norm = (s) => String(s || '').toLowerCase().replace(/ё/g, 'е').trim()

export function searchMenu(query, keys) {
  const words = norm(query).split(/\s+/).filter(Boolean)
  if (!words.length) return []
  return keys
    .map((key) => {
      const it = ITEMS[key]
      const name = norm(it.name)
      const hay = [name, norm(it.hint), norm(it.keywords)].join(' ')
      if (!words.every((w) => hay.includes(w))) return null
      const rank = words.reduce((r, w) => r + (name.startsWith(w) ? 3 : name.includes(w) ? 2 : 1), 0)
      return { key, rank }
    })
    .filter(Boolean)
    .sort((a, b) => b.rank - a.rank)
    .map((x) => x.key)
}

// Группы, раскрытые по умолчанию: где открыт текущий экран или где есть часто используемое.
export function defaultOpenGroups(menu, current) {
  return menu.groups
    .filter((g) => g.hasHot || g.items.some((i) => i.key === current))
    .map((g) => g.key)
}

// Бейдж: 0 → не показываем, больше 99 → «99+».
export function badgeText(n) {
  const v = Number(n) || 0
  if (v <= 0) return ''
  return v > 99 ? '99+' : String(v)
}
